import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { TourPlacement, TourStep } from "./tourSteps";
import { findVisibleTarget } from "./tourDom";

const CARD_WIDTH = 344;
const GAP = 16;
const MARGIN = 12;
const PAD = 6;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CardSpot {
  left: number;
  top: number;
  side: TourPlacement;
}

const FALLBACKS: Record<TourPlacement, TourPlacement[]> = {
  right: ["right", "bottom", "left", "top"],
  bottom: ["bottom", "left", "top", "right"],
  left: ["left", "bottom", "top", "right"],
  top: ["top", "bottom", "right", "left"],
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(v, max));

function placeCard(
  box: Box,
  cardH: number,
  placement: TourPlacement
): CardSpot {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const midY = box.top + box.height / 2 - cardH / 2;
  const midX = box.left + box.width / 2 - CARD_WIDTH / 2;
  const yIn = clamp(midY, MARGIN, vh - cardH - MARGIN);
  const xIn = clamp(midX, MARGIN, vw - CARD_WIDTH - MARGIN);

  const spots: Record<TourPlacement, CardSpot & { fits: boolean }> = {
    right: {
      left: box.left + box.width + GAP,
      top: yIn,
      side: "right",
      fits: box.left + box.width + GAP + CARD_WIDTH <= vw - MARGIN,
    },
    left: {
      left: box.left - GAP - CARD_WIDTH,
      top: yIn,
      side: "left",
      fits: box.left - GAP - CARD_WIDTH >= MARGIN,
    },
    bottom: {
      left: xIn,
      top: box.top + box.height + GAP,
      side: "bottom",
      fits: box.top + box.height + GAP + cardH <= vh - MARGIN,
    },
    top: {
      left: xIn,
      top: box.top - GAP - cardH,
      side: "top",
      fits: box.top - GAP - cardH >= MARGIN,
    },
  };

  for (const side of FALLBACKS[placement]) {
    if (spots[side].fits) return spots[side];
  }
  // Nothing fits (a huge target on a small screen): float it bottom-centre.
  return {
    left: (vw - CARD_WIDTH) / 2,
    top: vh - cardH - MARGIN * 2,
    side: "bottom",
  };
}

export default function TourOverlay({
  step,
  target,
  position,
  total,
  onNext,
  onBack,
  onSkip,
  onFinish,
}: {
  step: TourStep;
  /** The element to spotlight; null = a centred card. */
  target: HTMLElement | null;
  /** 0-based position among the steps of this run. */
  position: number;
  total: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  /** The last card's buttons: `addLead` = "Add my first lead". */
  onFinish: (choice: "addLead" | "explore") => void;
}) {
  const [trackedBox, setBox] = useState<Box | null>(null);
  const [cardH, setCardH] = useState(220);
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  const isWelcome = step.id === "welcome";
  const isDone = step.id === "done";

  // Follow the target every frame: pages settle, lists load, the header
  // swaps its search box — the spotlight stays glued to the element.
  useEffect(() => {
    if (!target) return;
    let raf = 0;
    let last = "";
    let el: HTMLElement | null = target;
    const tick = () => {
      if (el && !el.isConnected && step.target) el = findVisibleTarget(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        const sig = `${r.left}|${r.top}|${r.width}|${r.height}`;
        if (sig !== last) {
          last = sig;
          const left = clamp(r.left - PAD, 0, window.innerWidth);
          const top = clamp(r.top - PAD, 0, window.innerHeight);
          setBox({
            left,
            top,
            width: Math.min(r.width + PAD * 2, window.innerWidth - left),
            height: Math.min(r.height + PAD * 2, window.innerHeight - top),
          });
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [target, step.target]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [step.id, trackedBox]);

  useEffect(() => {
    primaryRef.current?.focus({ preventScroll: true });
  }, [step.id]);

  // A centred card has no target; a box left over from the last step is ignored.
  const box = target ? trackedBox : null;
  const spot = box ? placeCard(box, cardH, step.placement ?? "right") : null;

  // The little arrow on the card's edge points at the target's middle.
  let arrow: CSSProperties | null = null;
  if (box && spot) {
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    if (spot.side === "right" || spot.side === "left") {
      arrow = {
        top: clamp(cy - spot.top, 18, cardH - 18) - 6,
        [spot.side === "right" ? "left" : "right"]: -6,
      };
    } else {
      arrow = {
        left: clamp(cx - spot.left, 18, CARD_WIDTH - 18) - 6,
        [spot.side === "bottom" ? "top" : "bottom"]: -6,
      };
    }
  }

  const progress = total > 1 ? (position + 1) / total : 1;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="xale-tour-title"
    >
      <style>{TOUR_CSS}</style>

      {/* Click shield: the page waits while the tour talks. */}
      <div
        className={`absolute inset-0 ${box ? "" : "bg-[rgba(5,25,18,0.55)] backdrop-blur-[2px]"}`}
        style={{ animation: "xale-tour-fade 200ms ease-out" }}
      />

      {box && (
        <>
          <div
            className="xale-tour-spot pointer-events-none absolute rounded-[14px]"
            style={{
              left: box.left,
              top: box.top,
              width: box.width,
              height: box.height,
              boxShadow: "0 0 0 9999px rgba(5,25,18,0.55)",
            }}
          />
          <div
            className="xale-tour-spot xale-tour-ring pointer-events-none absolute rounded-[14px]"
            style={{
              left: box.left,
              top: box.top,
              width: box.width,
              height: box.height,
            }}
          />
        </>
      )}

      <div
        ref={cardRef}
        key={step.id}
        className={`absolute rounded-[18px] border border-[#d6ebe3] bg-white p-5 shadow-[0_24px_60px_-12px_rgba(5,25,18,0.35)] ${
          spot ? "xale-tour-card" : "xale-tour-card-center"
        }`}
        style={
          spot
            ? { width: CARD_WIDTH, left: spot.left, top: spot.top }
            : {
                width: Math.min(420, window.innerWidth - MARGIN * 2),
                left: "50%",
                top: "50%",
              }
        }
      >
        {arrow && (
          <span
            className="absolute size-3 rotate-45 border border-[#d6ebe3] bg-white"
            style={{
              ...arrow,
              clipPath:
                spot?.side === "right"
                  ? "polygon(0 0, 0 100%, 100% 100%)"
                  : spot?.side === "left"
                    ? "polygon(0 0, 100% 0, 100% 100%)"
                    : spot?.side === "bottom"
                      ? "polygon(0 0, 100% 0, 0 100%)"
                      : "polygon(100% 0, 100% 100%, 0 100%)",
            }}
            aria-hidden
          />
        )}

        {!isDone && (
          <button
            type="button"
            onClick={onSkip}
            aria-label="Close the tour"
            className="absolute right-3 top-3 flex size-7 items-center justify-center rounded-lg text-[#697571] transition-colors hover:bg-[#f1f6f4] hover:text-[#1e302a]"
          >
            <X className="size-4" />
          </button>
        )}

        <div
          className={`flex items-center justify-center rounded-2xl bg-[#eaf6f0] ${
            isWelcome || isDone ? "mb-4 size-14 text-[30px]" : "mb-3 size-11 text-[22px]"
          }`}
          aria-hidden
        >
          <span className={isWelcome ? "xale-tour-wave" : undefined}>{step.emoji}</span>
        </div>

        <p
          id="xale-tour-title"
          className={`pr-6 font-semibold tracking-[-0.3px] text-[#1e302a] ${
            isWelcome || isDone ? "text-[20px] leading-7" : "text-[16px] leading-6"
          }`}
        >
          {step.title}
        </p>
        <p className="mt-1.5 text-[13.5px] leading-[1.55] text-[#505e59]">{step.body}</p>

        {!isWelcome && !isDone && (
          <div className="mt-4 flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#eef3f1]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#2fa877] to-[#156548] transition-[width] duration-300"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <span className="shrink-0 text-[11px] font-medium tabular-nums text-[#697571]">
              {position + 1} / {total}
            </span>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          {isWelcome ? (
            <>
              <button
                type="button"
                onClick={onSkip}
                className="rounded-xl px-3 py-2 text-[13px] font-medium text-[#697571] transition-colors hover:bg-[#f1f6f4] hover:text-[#1e302a]"
              >
                I'll explore myself
              </button>
              <button
                ref={primaryRef}
                type="button"
                onClick={onNext}
                className="rounded-xl bg-[#218760] px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-[#156548]"
              >
                Show me around 🚀
              </button>
            </>
          ) : isDone ? (
            <>
              <button
                type="button"
                onClick={() => onFinish("explore")}
                className="rounded-xl px-3 py-2 text-[13px] font-medium text-[#697571] transition-colors hover:bg-[#f1f6f4] hover:text-[#1e302a]"
              >
                Explore on my own
              </button>
              <button
                ref={primaryRef}
                type="button"
                onClick={() => onFinish("addLead")}
                className="rounded-xl bg-[#218760] px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-[#156548]"
              >
                Add my first lead
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onSkip}
                className="rounded-xl px-2 py-2 text-[12.5px] font-medium text-[#697571] transition-colors hover:text-[#1e302a]"
              >
                Skip tour
              </button>
              <div className="flex items-center gap-2">
                {position > 0 && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="rounded-xl border border-[#e3ece8] px-3 py-2 text-[13px] font-medium text-[#1e302a] transition-colors hover:bg-[#f5faf8]"
                  >
                    Back
                  </button>
                )}
                <button
                  ref={primaryRef}
                  type="button"
                  onClick={onNext}
                  className="rounded-xl bg-[#218760] px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-[#156548]"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

const TOUR_CSS = `
@keyframes xale-tour-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes xale-tour-pop {
  from { opacity: 0; transform: translateY(6px) scale(0.98) }
  to { opacity: 1; transform: none }
}
@keyframes xale-tour-pop-center {
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.97) }
  to { opacity: 1; transform: translate(-50%, -50%) }
}
@keyframes xale-tour-pulse {
  0% { box-shadow: 0 0 0 0 rgba(47,168,119,0.55) }
  70% { box-shadow: 0 0 0 10px rgba(47,168,119,0) }
  100% { box-shadow: 0 0 0 0 rgba(47,168,119,0) }
}
@keyframes xale-tour-wave {
  0%, 60%, 100% { transform: rotate(0) }
  10%, 30% { transform: rotate(14deg) }
  20% { transform: rotate(-8deg) }
  40% { transform: rotate(-4deg) }
  50% { transform: rotate(10deg) }
}
.xale-tour-spot { transition: left 320ms cubic-bezier(.2,.8,.2,1), top 320ms cubic-bezier(.2,.8,.2,1), width 320ms cubic-bezier(.2,.8,.2,1), height 320ms cubic-bezier(.2,.8,.2,1); }
.xale-tour-ring { border: 2px solid #2fa877; animation: xale-tour-pulse 1.8s ease-out infinite; }
.xale-tour-card { animation: xale-tour-pop 260ms cubic-bezier(.2,.8,.2,1); transition: left 320ms cubic-bezier(.2,.8,.2,1), top 320ms cubic-bezier(.2,.8,.2,1); }
.xale-tour-card-center { transform: translate(-50%, -50%); animation: xale-tour-pop-center 280ms cubic-bezier(.2,.8,.2,1); }
.xale-tour-wave { display: inline-block; transform-origin: 70% 70%; animation: xale-tour-wave 2s ease-in-out 300ms 2; }
@media (prefers-reduced-motion: reduce) {
  .xale-tour-spot, .xale-tour-card, .xale-tour-card-center, .xale-tour-ring, .xale-tour-wave { animation: none !important; transition: none !important; }
}
`;
